<script lang="ts">
    import { faEdit, faPlusCircle, faUser } from '@fortawesome/free-solid-svg-icons'
    import { onDestroy, onMount } from 'svelte/internal'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import dayjs from 'dayjs'
    import type { Purchase } from '../../modules/paymentsApi/purchases/purchase.entity'
    import { PurchaseStatus, PurchaseStatusFriendly } from '../../modules/paymentsApi/purchases/purchase.enum'
    import Popover from '../_common/popover.svelte'
    import PaymentsPurchaseShow from './payments-purchase-show.svelte'
    import { GridBadge, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import Zgrid2 from '../_common/z/zgrid2.svelte'
    // só os acts do Zgrid2 vêm do zgrid2.ts: "active" (grupo segmentado) não existe no GridButton do zgrid.ts
    import { GridButton as Grid2Button, GridItemShow as Grid2ItemShow } from '../_common/z/zgrid2'
    import PaymentsEmit from './payments-emit.svelte'

    let show = false
    let entities: Purchase[] = []
    let total = 0

    let dates: { from: Date; to: Date } = null
    function showDate() {
        dates = window.api.utils.dates.currentMonth()
    }

    onMount(async () => {
        await loadEntities()
        await loadFromParam()
    })

    function loadFromParam() {
        const urlParams = new URLSearchParams(window.location.search)
        const id = urlParams.get('id')
        if (!id) return
        showPurchaseDetail(id)
    }

    async function loadEntities() {
        try {
            show = true
            total = 0
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                status: filterStatus || undefined,
                charges: false,
            }
            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.pay.entries.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (!filter.from || diff > 7) {
                    filter.from = dayjs(new Date()).add(-7, 'day').toDate()
                }
            }
            entities = await window.api.payments.purchases.getAll(filter)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities, 'referenceId')
            total = entities.reduce((total, item) => {
                if (item.purchaseTotalValue) total += item.purchaseTotalValue
                return total
            }, 0)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Cobranças Emitidas'
        grid.subtitle = window.store.token.permissions.pay.entries.navigate ? '' : 'Lançamentos dos últimos 7 dias.'
        grid.fontSize = '13px'
        // a barra de filtros e o "Novo" do topo são acts do grid: filtros no grupo segmentado, "Novo" no cabeçalho
        if (window.store.token.permissions.pay.entries.navigate) {
            const filters: [string, string, boolean][] = [
                ['all', 'Todos', false],
                ['active', 'Ativos', false],
                ['overdue', 'Atrasados', false],
                ['pending', 'Pendentes', true],
                ['canceled', 'Cancelados', true],
            ]
            for (const [type, label, hideOnMobile] of filters) {
                grid.acts.push(
                    new Grid2Button({
                        label,
                        active: filterCurrent === type,
                        mobile: hideOnMobile ? Grid2ItemShow.hide : Grid2ItemShow.show,
                        callback: () => loadFromFilter(type),
                    }),
                )
            }
            // "Por data" é act type "date": abre o seletor de período no próprio grid e só filtra depois de escolhido
            grid.acts.push(
                new Grid2Button({
                    label: 'Por data',
                    type: 'date',
                    active: filterCurrent === 'date',
                    dateFrom: filterCurrent === 'date' ? dates?.from : null,
                    dateTo: filterCurrent === 'date' ? dates?.to : null,
                    callback: (period: { from: Date; to: Date }) => loadFromPeriod(period),
                }),
            )
        }
        grid.acts.push(new Grid2Button({ label: 'Novo', icon: faPlusCircle, color: 'primary', callback: () => (showEmit = true) }))
        grid.cols.push(new GridCol({ content: 'Data', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Nome', type: GridColTypeEnum.string, maxLength: 30 }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string, maxLength: 40 }))
        grid.cols.push(new GridCol({ content: 'ID', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'Atraso', type: GridColTypeEnum.int }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.person?.personId),
                icon: faUser,
                // só o 1º botão da linha fica à mostra; os demais vão pro menu "⋯", onde precisam de texto
                label: 'Pessoa',
            })
            const btnOpen = new GridButton({
                callback: () => showPurchaseDetail(item.purchaseId),
                icon: faEdit,
                label: 'Abrir',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.createdAt })
            // a assinatura só vira 'cancelado' na data agendada; até lá o único sinal é a data
            const cancelling = !!item.scheduledCancelAt && item.status !== PurchaseStatus.canceled
            line.slots.push({
                content: new GridBadge({
                    label: PurchaseStatusFriendly[item.statusLabel] + (cancelling ? ' · encerrando' : ''),
                    class: 'status_' + item.status,
                }),
            })
            line.slots.push({ content: item.person?.name || '[cliente não informado]' })
            line.slots.push({ content: item.purchaseDescription })
            line.slots.push({ content: item.purchaseId })
            line.slots.push({ content: item.pastDueDays })
            line.slots.push({ content: item.purchaseTotalValue })
            // o "Abrir" é a ação principal (à mostra) e "Pessoa" vai pro menu
            line.slots.push({ content: [btnOpen, btnPerson] })
            grid.lines.push(line)
        }
    }

    let showEmit = false

    let purchaseId = null
    function showPurchaseDetail(id: string) {
        purchaseId = id
    }

    function closePurchaseShow() {
        purchaseId = null
        loadEntities()
    }

    function cancelPurchaseShow() {
        purchaseId = null
    }

    let filterStatus: PurchaseStatus = null
    let filterCurrent = 'all'
    function loadFromFilter(type) {
        filterCurrent = type
        if (type === 'all') {
            dates = null
            filterStatus = null
        }
        if (type === 'active') {
            dates = null
            filterStatus = PurchaseStatus.active
        }
        if (type === 'overdue') {
            dates = null
            filterStatus = PurchaseStatus.overdue
        }
        if (type === 'pending') {
            dates = null
            filterStatus = PurchaseStatus.pending
        }
        if (type === 'canceled') {
            dates = null
            filterStatus = PurchaseStatus.canceled
        }
        if (type === 'date') {
            showDate()
            filterStatus = null
        }
        loadEntities()
    }

    // "Por data" no Zgrid2: o act type "date" já entrega o período escolhido no popup
    function loadFromPeriod(period: { from: Date; to: Date }) {
        filterCurrent = 'date'
        filterStatus = null
        dates = period
        loadEntities()
    }
</script>

{#if purchaseId}
    <Popover title="Cobrança" width="1100px" height="80vh" on:close={cancelPurchaseShow}>
        <PaymentsPurchaseShow {purchaseId} on:close={closePurchaseShow} />
    </Popover>
{/if}

{#if showEmit}
    <Popover
        title="Nova cobrança"
        width="920px"
        height="620px"
        on:close={() => {
            showEmit = false
            loadEntities()
        }}
    >
        <PaymentsEmit />
    </Popover>
{/if}

<Content footer={true}>
    <!-- os filtros, o período ("Por data") e o "Novo" são acts do grid (ver loadGrid), por isso não há barra própria aqui -->
    <Zgrid2 {grid} class="h-space" />
    <LoadSpinner {show} />
</Content>

<style>
</style>
